import { useCallback, useMemo, useRef, useState } from 'react';

import { AnimatedIcon } from '@components/primitives/AnimatedIcon';
import { Button, ButtonProps } from '@components/primitives/Button';
import { Tooltip } from '@components/primitives/Tooltip';

import css from './styles.module.css';

export async function copyText(text: string) {
  await navigator.clipboard.writeText(text.trimEnd());
}

interface UseCopyToClipboardOptions {
  timeoutDelay?: number;
}

interface UseCopyToClipboardResult {
  copy: (content: string) => Promise<void>;
  copied: boolean;
}

export default function useCopyToClipboard(
  options?: UseCopyToClipboardOptions
): UseCopyToClipboardResult {
  const timer = useRef<NodeJS.Timeout | null>(null);
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    async (content: string) => {
      if (timer.current) clearTimeout(timer.current);

      await copyText(content);
      setCopied(true);

      timer.current = setTimeout(
        () => setCopied(false),
        options?.timeoutDelay ?? 2000
      );
    },
    [options?.timeoutDelay]
  );

  return useMemo(() => ({ copy, copied }), [copy, copied]);
}

export interface CopyButtonProps {
  title: string;
  onCopy?(): void;
  value?: string | (() => Promise<string>);
  ghost?: boolean;
  className?: string;
  size?: ButtonProps['size'];
  flush?: ButtonProps['flush'];
  tooltip?: boolean;
}

export function CopyButton({
  title,
  onCopy,
  value,
  size,
  flush,
  className,
  ghost = true,
  tooltip = true,
}: CopyButtonProps) {
  const clipboard = useCopyToClipboard();

  async function onClick() {
    if (!value) return;
    const content = typeof value === 'function' ? await value() : value;
    await clipboard.copy(content);
    onCopy?.();
  }

  return (
    <Tooltip content={tooltip ? title : undefined} side="top">
      <Button
        type="button"
        variant="secondary"
        size={size}
        flush={flush}
        ghost={ghost}
        aria-label={title}
        className={className}
        onClick={onClick}
      >
        <span className={css.icon}>
          <AnimatedIcon.CopyCheck state={clipboard.copied ? 'check' : 'copy'} />
        </span>
      </Button>
    </Tooltip>
  );
}
